import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { EndpointService } from "./endpoint.service";
import { IGridService } from "../shared/grid/grid.service";
import { GridModel } from "../shared/grid/grid.model";
import { ToastService } from "./toast.service";
import { catchError } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { CustomEntity, CustomEntityModel } from "../models/customEntity.model";
import { CustomObjectLookup } from "../models/customObjectLookup.model";
import { SearchResultObject } from "../models/searchResultObject.model";
import { EndpointLookup } from "../models/endpointLookup.model";
import { NodeOverviewData } from "../models/nodeOverviewData.model";
import { ColumnDetails } from "../models/columnDetails.model";
import { GridExport } from "../models/gridExport.model";
import { map } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class CustomEntityService
    extends EndpointService
    implements IGridService<CustomEntity>
{
    constructor(
        http: HttpClient,
        private toastService: ToastService,
    ) {
        super(http);
    }

    get(
        filter: string,
        orderBy: string,
        sort: string,
        page: number,
        pageSize: number,
        filterBy: string,
        headers?: HttpHeaders,
    ) {
        return this.http.get<GridModel<CustomEntity>>(
            this.getApiUrl("CustomEntity/GetCustomEntities"),
            {
                params: this.createParamsWithFilterBy(
                    filter,
                    orderBy,
                    sort,
                    page,
                    pageSize,
                    filterBy,
                ),
                headers: headers,
            },
        );
    }

    getFieldConfiguration(
        coId?: string,
        suppressErrors: boolean = true,
    ): Observable<{ [property: string]: ColumnDetails }> {
        const headers = coId
            ? new HttpHeaders().set("coId", coId.toString())
            : undefined;
        const request$ = this.http
            .get<{
                [property: string]: ColumnDetails;
            }>(this.getApiUrl("CustomEntity/GetFieldConfiguration"), { headers })
            .pipe(map((res) => this.applyTypeNormalization(res)));

        if (!suppressErrors) {
            return request$;
        }

        return request$.pipe(
            catchError((error) => {
                console.error("Error loading field configuration:", error);
                return of({});
            }),
        );
    }

    export(request: GridExport, headers?: HttpHeaders): Observable<HttpResponse<Blob>> {
        return this.http.post(
            this.getApiUrl("CustomEntity/Export"),
            request,
            { responseType: "blob", observe: "response", headers },
        );
    }

    private applyTypeNormalization(details: {
        [property: string]: ColumnDetails;
    }): { [property: string]: ColumnDetails } {
        if (!details) return details;
        const mapType = (raw: string): string => {
            if (!raw) return "string";
            const t = raw.toLowerCase();
            if (
                [
                    "decimal",
                    "double",
                    "integer",
                    "int",
                    "long",
                    "number",
                    "float",
                ].includes(t)
            )
                return "number";
            if (t === "datetime" || t === "date") return "date";
            if (t === "boolean" || t === "bool") return "boolean";
            return "string";
        };
        Object.keys(details).forEach((k) => {
            details[k].type = mapType(details[k].type);
        });
        return details;
    }

    getLowBatteryEndpointCount(batteryLevel: number, headers?: HttpHeaders) {
        const options = {
            headers: headers,
        };

        return this.http
            .get<number>(
                this.getApiUrl(
                    `CustomEntity/GetLowBatteryEndpointCount?batteryLevel=${batteryLevel}`,
                ),
                options,
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading low battery node count",
                        true,
                    );
                    return of();
                }),
            );
    }

    getCustomEntity(id: string, coId?: number | string) {
        const headers = coId
            ? new HttpHeaders().set("coId", coId.toString())
            : undefined;
        return this.http
            .get<CustomEntity>(
                this.getApiUrl(`CustomEntity/GetCustomEntity?id=${id}`),
                { headers },
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading custom entity",
                        true,
                    );
                    return of(null);
                }),
            );
    }

    getCustomEntityQuietly(id: string, coId?: number | string): Observable<CustomEntity | null> {
        const headers = coId
            ? new HttpHeaders().set("coId", coId.toString())
            : undefined;
        return this.http
            .get<CustomEntity>(
                this.getApiUrl(`CustomEntity/GetCustomEntity?id=${id}`),
                { headers },
            )
            .pipe(catchError(() => of(null)));
    }

    create(payload: CustomEntityModel) {
        const headers = payload.customObjectId
            ? new HttpHeaders().set("coId", payload.customObjectId.toString())
            : undefined;
        return this.http.post<number>(
            this.getApiUrl("CustomEntity/CreateCustomEntity"),
            payload,
            { headers },
        );
    }

    update(payload: CustomEntityModel) {
        const headers = payload.customObjectId
            ? new HttpHeaders().set("coId", payload.customObjectId.toString())
            : undefined;
        return this.http.post<number>(
            this.getApiUrl("CustomEntity/UpdateCustomEntity"),
            payload,
            { headers },
        );
    }

    delete(id: string) {
        return this.http.delete(
            this.getApiUrl(`CustomEntity/DeleteCustomEntity?id=${id}`),
        );
    }

    getEndPointLookupList() {
        return this.http
            .get<
                EndpointLookup[]
            >(this.getApiUrl("CustomEntity/GetEndPointLookupList"))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading end points",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getEndPointLookupListByNotificationId(id: string) {
        return this.http
            .get<
                EndpointLookup[]
            >(this.getApiUrl(`CustomEntity/GetEndPointLookupListByNotificationId?notificationSettingId=${id}`))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading end points",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getIconLookup(id: string) {
        return this.http
            .get<CustomObjectLookup>(
                this.getApiUrl(`CustomEntity/GetIconLookup?id=${id}`),
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading entity",
                        true,
                    );
                    return of(new CustomObjectLookup());
                }),
            );
    }

    search(searchText: any) {
        return this.http
            .get<
                GridModel<SearchResultObject>
            >(this.getApiUrl(`CustomEntity/GetSearchResults?filter=${searchText}`))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error searching entity",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getNodeOverviewData(coId?: string): Observable<NodeOverviewData> {
        const headers = coId
            ? new HttpHeaders().set("coId", coId.toString())
            : undefined;
        return this.http
            .get<NodeOverviewData>(
                this.getApiUrl("CustomEntity/GetNodeOverviewData"),
                { headers },
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading node overview data",
                        true,
                    );
                    return of();
                }),
            );
    }
}
